import type { JSONContent } from '@tiptap/react';

export interface PortableTextChild {
  _key: string;
  _type: string;
  marks: string[];
  text: string;
}

export interface PortableTextMarkDef {
  _key: string;
  _type: string;
  href?: string;
}

export interface PortableTextBlock {
  _key: string;
  _type: 'block';
  children: PortableTextChild[];
  markDefs: PortableTextMarkDef[];
  style: string;
  listItem?: 'bullet' | 'number';
  level?: number;
}

export type PortableTextContent = PortableTextBlock[];

let keyCounter = 0;
const generateKey = (): string => {
  keyCounter += 1;
  return `k${keyCounter.toString(36)}${Date.now().toString(36)}`;
};

export const resetKeyCounter = (): void => {
  keyCounter = 0;
};

const getStyleFromNode = (node: JSONContent): string => {
  switch (node.type) {
    case 'heading':
      return `h${node.attrs?.level || 1}`;
    case 'paragraph':
    default:
      return 'normal';
  }
};

const getMarksFromNode = (node: JSONContent): string[] => {
  const marks: string[] = [];
  if (node.marks) {
    for (const mark of node.marks) {
      if (mark.type === 'bold') marks.push('strong');
      else if (mark.type === 'italic') marks.push('em');
      else if (mark.type === 'underline') marks.push('underline');
      else if (mark.type === 'link') marks.push(mark.attrs?.href || '');
    }
  }
  return marks;
};

const extractLinkMarkDefs = (
  children: JSONContent[],
): { markDefs: PortableTextMarkDef[]; linkKeyMap: Map<string, string> } => {
  const markDefs: PortableTextMarkDef[] = [];
  const linkKeyMap = new Map<string, string>();

  const processNode = (node: JSONContent) => {
    if (node.marks) {
      for (const mark of node.marks) {
        if (mark.type === 'link' && mark.attrs?.href) {
          const href = mark.attrs.href;
          if (!linkKeyMap.has(href)) {
            const key = generateKey();
            linkKeyMap.set(href, key);
            markDefs.push({
              _key: key,
              _type: 'link',
              href,
            });
          }
        }
      }
    }
    if (node.content) {
      for (const child of node.content) {
        processNode(child);
      }
    }
  };

  for (const child of children) {
    processNode(child);
  }

  return { markDefs, linkKeyMap };
};

const convertTextChildren = (
  children: JSONContent[],
  linkKeyMap: Map<string, string>,
): PortableTextChild[] => {
  const result: PortableTextChild[] = [];

  const processNode = (node: JSONContent) => {
    if (node.type === 'text') {
      const marks: string[] = [];
      if (node.marks) {
        for (const mark of node.marks) {
          if (mark.type === 'bold') marks.push('strong');
          else if (mark.type === 'italic') marks.push('em');
          else if (mark.type === 'underline') marks.push('underline');
          else if (mark.type === 'link' && mark.attrs?.href) {
            const linkKey = linkKeyMap.get(mark.attrs.href);
            if (linkKey) marks.push(linkKey);
          }
        }
      }
      result.push({
        _key: generateKey(),
        _type: 'span',
        marks,
        text: node.text || '',
      });
    } else if (node.type === 'hardBreak') {
      result.push({
        _key: generateKey(),
        _type: 'span',
        marks: [],
        text: '\n',
      });
    }
  };

  for (const child of children) {
    if (child.type === 'text' || child.type === 'hardBreak') {
      processNode(child);
    }
  }

  return result;
};

const convertBlockNode = (
  node: JSONContent,
  listItem?: 'bullet' | 'number',
  level?: number,
): PortableTextBlock | null => {
  if (!node.content || node.content.length === 0) {
    return {
      _key: generateKey(),
      _type: 'block',
      children: [{ _key: generateKey(), _type: 'span', marks: [], text: '' }],
      markDefs: [],
      style: getStyleFromNode(node),
      ...(listItem && { listItem }),
      ...(level && { level }),
    };
  }

  const { markDefs, linkKeyMap } = extractLinkMarkDefs(node.content);
  const children = convertTextChildren(node.content, linkKeyMap);

  if (children.length === 0) {
    children.push({ _key: generateKey(), _type: 'span', marks: [], text: '' });
  }

  return {
    _key: generateKey(),
    _type: 'block',
    children,
    markDefs,
    style: getStyleFromNode(node),
    ...(listItem && { listItem }),
    ...(level && { level }),
  };
};

export const tiptapToPortableText = (
  content: JSONContent,
): PortableTextContent => {
  resetKeyCounter();
  const blocks: PortableTextContent = [];

  if (!content.content) return blocks;

  for (const node of content.content) {
    if (node.type === 'paragraph' || node.type === 'heading') {
      const block = convertBlockNode(node);
      if (block) blocks.push(block);
    } else if (node.type === 'bulletList') {
      if (node.content) {
        for (const listItem of node.content) {
          if (listItem.type === 'listItem' && listItem.content) {
            for (const child of listItem.content) {
              const block = convertBlockNode(child, 'bullet', 1);
              if (block) blocks.push(block);
            }
          }
        }
      }
    } else if (node.type === 'orderedList') {
      if (node.content) {
        for (const listItem of node.content) {
          if (listItem.type === 'listItem' && listItem.content) {
            for (const child of listItem.content) {
              const block = convertBlockNode(child, 'number', 1);
              if (block) blocks.push(block);
            }
          }
        }
      }
    }
  }

  return blocks;
};

const portableTextStyleToTiptapType = (
  style: string,
): { type: string; attrs?: Record<string, unknown> } => {
  if (style.startsWith('h')) {
    const level = parseInt(style.slice(1), 10);
    return { type: 'heading', attrs: { level } };
  }
  return { type: 'paragraph' };
};

const convertPortableTextMarks = (
  marks: string[],
  markDefs: PortableTextMarkDef[],
): { type: string; attrs?: Record<string, unknown> }[] => {
  const result: { type: string; attrs?: Record<string, unknown> }[] = [];
  const markDefMap = new Map(markDefs.map((md) => [md._key, md]));

  for (const mark of marks) {
    if (mark === 'strong') {
      result.push({ type: 'bold' });
    } else if (mark === 'em') {
      result.push({ type: 'italic' });
    } else if (mark === 'underline') {
      result.push({ type: 'underline' });
    } else {
      const linkDef = markDefMap.get(mark);
      if (linkDef?.href) {
        result.push({ type: 'link', attrs: { href: linkDef.href } });
      }
    }
  }

  return result;
};

export const portableTextToTiptap = (
  blocks: PortableTextContent,
): JSONContent => {
  if (!blocks || !Array.isArray(blocks)) {
    return { type: 'doc', content: [] };
  }

  const content: JSONContent[] = [];
  let currentList: {
    type: 'bulletList' | 'orderedList';
    items: JSONContent[];
  } | null = null;

  const flushList = () => {
    if (currentList) {
      content.push({
        type: currentList.type,
        content: currentList.items,
      });
      currentList = null;
    }
  };

  for (const block of blocks) {
    if (!block || block._type !== 'block') continue;

    const { type, attrs } = portableTextStyleToTiptapType(
      block.style || 'normal',
    );
    const textContent: JSONContent[] = [];
    const children = block.children || [];
    const markDefs = block.markDefs || [];

    for (const child of children) {
      if (!child) continue;
      if (child._type === 'span') {
        if (child.text === '\n') {
          textContent.push({ type: 'hardBreak' });
        } else if (child.text && child.text.length > 0) {
          const marks = convertPortableTextMarks(child.marks || [], markDefs);
          textContent.push({
            type: 'text',
            text: child.text,
            ...(marks.length > 0 && { marks }),
          });
        }
      }
    }

    const paragraphNode: JSONContent = {
      type,
      ...(attrs && { attrs }),
      ...(textContent.length > 0 && { content: textContent }),
    };

    if (block.listItem) {
      const listType =
        block.listItem === 'bullet' ? 'bulletList' : 'orderedList';
      const listItemNode: JSONContent = {
        type: 'listItem',
        content: [paragraphNode],
      };

      if (currentList && currentList.type === listType) {
        currentList.items.push(listItemNode);
      } else {
        flushList();
        currentList = { type: listType, items: [listItemNode] };
      }
    } else {
      flushList();
      content.push(paragraphNode);
    }
  }

  flushList();

  return {
    type: 'doc',
    content,
  };
};
